import { Currency } from "@hubrise/react-components"
import * as React from "react"

import { useStatefulFieldRemount } from "Catalog/shared/components/StatefulFieldRemountContext"
import { useConfig } from "shared/components/ConfigContext"
import { useEditorStoreContext } from "shared/components/EditorStoreContext"
import PageError from "shared/components/PageError"
import PageSpinner from "shared/components/PageSpinner"
import { useCatalogQuery } from "shared/hooks/queries"
import { Editor } from "shared/hubrise/editor"
import { ICatalog } from "shared/hubrise/models"
import { pathAndSearchQuery } from "shared/utils/url"

export type EditorContextInterface = {
  /** The editor for the current catalog */
  editor: Editor
  /** Must be called when the editor's hash changes. */
  onChange: () => void
  /** Causes the consumer to re-render when this hash changes. */
  editorHash: string
  /** Force a refetch of the current catalog. */
  refetchCatalog: () => Promise<void>
  /** Is a catalog loading? */
  isFetching: boolean
}

const EditorContext = React.createContext<EditorContextInterface | undefined>(undefined)

interface EditorContextProviderProps {
  catalogId: string
  currency: Currency
  children: React.ReactNode
}

export const EditorContextProvider = ({ catalogId, currency, children }: EditorContextProviderProps): JSX.Element => {
  const { appInstanceId } = useConfig()
  const { openEditors, setEditor, onChange } = useEditorStoreContext()
  const { remount } = useStatefulFieldRemount()

  const onServerChange = React.useCallback(
    (serverCatalog: ICatalog) => {
      const editor = new Editor(serverCatalog, onChange, currentUrl)
      setEditor(editor, remount)
    },
    [onChange, remount, setEditor],
  )

  const { error: fetchError, refetch, isFetching } = useCatalogQuery(catalogId, appInstanceId, currency, onServerChange)

  const refetchCatalog = React.useCallback(async () => {
    await refetch()
  }, [refetch])

  const editor: Editor | undefined = openEditors[catalogId]
  const editorHash = editor?.hash ?? ""
  const value: EditorContextInterface = React.useMemo(
    () => ({ editor, onChange, editorHash, refetchCatalog, isFetching }),
    [editor, onChange, editorHash, refetchCatalog, isFetching],
  )

  if (fetchError) return <PageError error={fetchError} />
  if (editor === undefined) return <PageSpinner />

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>
}

export function useEditorContext(): EditorContextInterface {
  const context = React.useContext(EditorContext)

  if (context === undefined) {
    throw new Error(`useEditorContext must be used within EditorContextProvider`)
  }

  return context
}

const currentUrl = (): string => pathAndSearchQuery()
